import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { FileBarChart, Users, AlertCircle, UserMinus, Download, Printer, Calendar, TrendingUp, ArrowUpRight, RefreshCw, Ticket, CheckCircle2, Clock, Wrench, ArrowUpDown, ArrowUp, ArrowDown, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

const fmtIDR = (n) => 'Rp ' + (Number(n) || 0).toLocaleString('id-ID');
const fmtDate = (iso) => { if (!iso) return '-'; try { return new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; } };

const PeriodPicker = ({ year, month, onChange, loading }) => {
  const now = new Date();
  const years = []; for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(y);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select value={year} onChange={e => onChange(Number(e.target.value), month)} disabled={loading}
        className="h-9 px-3 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select value={month} onChange={e => onChange(year, Number(e.target.value))} disabled={loading}
        className="h-9 px-3 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'indigo', sub }) => (
  <Card className="p-4 border-0 shadow-sm bg-white dark:bg-slate-900 dark:ring-1 dark:ring-slate-700">
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
        <div className={`text-2xl font-bold mt-1 text-${color}-600 dark:text-${color}-400`}>{value}</div>
        {sub && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${color}-100 dark:bg-${color}-900/30`}>
        <Icon className={`w-4 h-4 text-${color}-600 dark:text-${color}-400`} />
      </div>
    </div>
  </Card>
);

const exportToExcel = (filename, rows, columns) => {
  // Build CSV (Excel-compatible)
  const header = columns.map(c => `"${c.label}"`).join(',');
  const body = rows.map(r => columns.map(c => {
    let v = typeof c.value === 'function' ? c.value(r) : r[c.value];
    if (v == null) v = '';
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  }).join(',')).join('\n');
  const csv = '\uFEFF' + header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '.csv';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const printReport = (title, period, rows, columns, summary) => {
  const w = window.open('', '_blank');
  if (!w) return;
  const tbl = `<table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:10px">
    <thead><tr>${columns.map(c => `<th style="border:1px solid #ccc;padding:6px;background:#f1f5f9;text-align:left">${c.label}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(r => `<tr>${columns.map(c => {
      let v = typeof c.value === 'function' ? c.value(r) : r[c.value];
      return `<td style="border:1px solid #ccc;padding:5px">${v ?? '-'}</td>`;
    }).join('')}</tr>`).join('')}</tbody>
  </table>`;
  const summaryHtml = summary ? `<div style="margin-top:12px;padding:10px;background:#f8fafc;border-radius:6px">${summary}</div>` : '';
  w.document.write(`<html><head><title>${title}</title></head><body style="font-family:Arial,sans-serif;padding:20px">
    <h2 style="margin:0">${title}</h2>
    <p style="color:#666;margin:4px 0">Periode: ${period}</p>
    ${summaryHtml}${tbl}
    <script>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
};

const ReportsPage = () => {
  const { selectedBranch } = useApp();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState('registrations');

  const [regData, setRegData] = useState(null);
  const [lateData, setLateData] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(false);

  const periodLabel = `${MONTHS[month - 1]} ${year}`;

  const fetchAll = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const bq = (selectedBranch && selectedBranch !== 'all') ? `&branch_id=${selectedBranch}` : '';
    try {
      const [r, l, c, t] = await Promise.all([
        axios.get(`${API_URL}/api/v2/reports/registrations?year=${year}&month=${month}${bq}`, { headers }),
        axios.get(`${API_URL}/api/v2/reports/late-payments?year=${year}&month=${month}${bq}`, { headers }),
        axios.get(`${API_URL}/api/v2/reports/churned-customers?year=${year}&month=${month}${bq}`, { headers }),
        axios.get(`${API_URL}/api/v2/reports/tickets-problems?year=${year}&month=${month}${bq}`, { headers }),
      ]);
      if (r.data?.success) setRegData(r.data.data);
      if (l.data?.success) setLateData(l.data.data);
      if (c.data?.success) setChurnData(c.data.data);
      if (t.data?.success) setTicketData(t.data.data);
    } catch (e) {
      toast.error('Gagal memuat laporan: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [year, month, selectedBranch]);

  const onPeriodChange = (y, m) => { setYear(y); setMonth(m); };

  // ======== COLUMNS ========
  const regCols = [
    { label: 'Nama', value: 'name' },
    { label: 'No Akun', value: 'account_number' },
    { label: 'Username', value: 'pppoe_username' },
    { label: 'Paket', value: 'service_plan' },
    { label: 'Biaya', value: r => fmtIDR(r.monthly_fee) },
    { label: 'Cabang', value: 'branch_name' },
    { label: 'Pemilik', value: 'owner' },
    { label: 'Tgl Registrasi', value: r => fmtDate(r.reg_date) },
  ];

  const lateCols = [
    { label: 'Invoice', value: 'invoice_number' },
    { label: 'Pelanggan', value: 'customer_name' },
    { label: 'No Akun', value: 'account_number' },
    { label: 'Cabang', value: 'branch_name' },
    { label: 'Pemilik', value: 'owner' },
    { label: 'Jumlah', value: r => fmtIDR(r.total_amount) },
    { label: 'Jatuh Tempo', value: r => fmtDate(r.due_date) },
    { label: 'Tgl Bayar', value: r => fmtDate(r.paid_date) },
    { label: 'Telat (hari)', value: 'late_days' },
    { label: 'Kategori', value: 'reason' },
  ];

  const churnCols = [
    { label: 'Nama', value: 'name' },
    { label: 'No Akun', value: 'account_number' },
    { label: 'Paket', value: 'service_plan' },
    { label: 'Biaya', value: r => fmtIDR(r.monthly_fee) },
    { label: 'Cabang', value: 'branch_name' },
    { label: 'Pemilik', value: 'owner' },
    { label: 'Tgl Registrasi', value: r => fmtDate(r.reg_date) },
    { label: 'Tgl Berhenti', value: r => fmtDate(r.end_date) },
  ];


  const ticketCols = [
    { label: 'No Tiket', value: 'ticket_number' },
    { label: 'Judul', value: 'title' },
    { label: 'Pelanggan', value: 'customer_name' },
    { label: 'Kategori', value: 'category' },
    { label: 'Prioritas', value: 'priority' },
    { label: 'Status', value: 'status' },
    { label: 'Teknisi', value: 'technician' },
    { label: 'Cabang', value: 'branch_name' },
    { label: 'Tgl Buka', value: r => fmtDate(r.created_at) },
    { label: 'Tgl Selesai', value: r => fmtDate(r.resolved_at) },
    { label: 'Durasi (jam)', value: r => r.resolution_hours != null ? r.resolution_hours.toFixed(1) : '-' },
  ];

  const TabHeader = ({ title, icon: Icon, subtitle, onExport, onPrint, rows = [] }) => (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          {title}
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={onExport} disabled={!rows.length} data-testid={`export-${title}`}>
          <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
        </Button>
        <Button size="sm" variant="outline" onClick={onPrint} disabled={!rows.length}>
          <Printer className="w-3.5 h-3.5 mr-1.5" /> Print/PDF
        </Button>
      </div>
    </div>
  );

  // DataTable with sort, pagination, search (2026-05-20)
  const DataTable = ({ rows, cols, testid }) => {
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState('');

    const filteredRows = useMemo(() => {
      if (!search.trim()) return rows;
      const q = search.toLowerCase();
      return rows.filter(r => cols.some(c => {
        const v = typeof c.value === 'function' ? c.value(r) : r[c.value];
        return String(v ?? '').toLowerCase().includes(q);
      }));
    }, [rows, cols, search]);

    const sortedRows = useMemo(() => {
      if (!sortKey) return filteredRows;
      const col = cols.find(c => (c.value === sortKey) || (typeof c.value === 'function' && c.label === sortKey));
      if (!col) return filteredRows;
      const arr = [...filteredRows];
      arr.sort((a, b) => {
        const rawA = col.sortKey ? a[col.sortKey] : (typeof col.value === 'function' ? col.value(a) : a[col.value]);
        const rawB = col.sortKey ? b[col.sortKey] : (typeof col.value === 'function' ? col.value(b) : b[col.value]);
        // numeric compare if both numeric-ish
        const nA = Number(String(rawA).replace(/[^\d.-]/g, ''));
        const nB = Number(String(rawB).replace(/[^\d.-]/g, ''));
        let cmp;
        if (!Number.isNaN(nA) && !Number.isNaN(nB) && String(rawA).match(/\d/) && String(rawB).match(/\d/)) {
          cmp = nA - nB;
        } else {
          cmp = String(rawA ?? '').localeCompare(String(rawB ?? ''), 'id');
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });
      return arr;
    }, [filteredRows, cols, sortKey, sortDir]);

    const total = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * perPage;
    const pagedRows = sortedRows.slice(start, start + perPage);

    const toggleSort = (col) => {
      const key = col.sortKey || (typeof col.value === 'string' ? col.value : col.label);
      if (sortKey === key) {
        setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
      setPage(1);
    };

    return (
      <Card className="overflow-hidden bg-white dark:bg-slate-900 dark:ring-1 dark:ring-slate-700 border-0 shadow-sm">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <span>Tampilkan</span>
            <select
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="h-7 px-2 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
              data-testid={`${testid}-per-page`}
            >
              {[10, 25, 50, 100, 250].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>entri</span>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Cari di tabel..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="h-7 pl-7 pr-2 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 w-48"
              data-testid={`${testid}-search`}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid={testid}>
            <thead className="bg-slate-100 dark:bg-slate-800">
              <tr>
                {cols.map((c, i) => {
                  const key = c.sortKey || (typeof c.value === 'string' ? c.value : c.label);
                  const isSorted = sortKey === key;
                  return (
                    <th
                      key={i}
                      className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 select-none"
                      onClick={() => toggleSort(c)}
                      data-testid={`${testid}-th-${i}`}
                    >
                      <div className="flex items-center gap-1">
                        {c.label}
                        {isSorted ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-slate-400 italic dark:text-slate-500">{search ? 'Tidak ada hasil pencarian' : 'Tidak ada data untuk periode ini'}</td></tr>
              ) : pagedRows.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  {cols.map((c, i) => {
                    const v = typeof c.value === 'function' ? c.value(r) : r[c.value];
                    return <td key={i} className="px-3 py-1.5 text-slate-700 dark:text-slate-300 whitespace-nowrap">{v ?? '-'}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 dark:border-slate-700 flex-wrap text-xs">
            <span className="text-slate-600 dark:text-slate-400">
              Menampilkan {start + 1}-{Math.min(start + perPage, total)} dari {total} entri
              {search && rows.length !== total && <span className="text-slate-400 dark:text-slate-500"> (difilter dari {rows.length})</span>}
            </span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={safePage === 1} onClick={() => setPage(safePage - 1)} className="h-7 px-2" data-testid={`${testid}-prev`}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="px-2 text-slate-700 dark:text-slate-300">Halaman {safePage} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)} className="h-7 px-2" data-testid={`${testid}-next`}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderTable = (rows, cols, testid) => <DataTable rows={rows} cols={cols} testid={testid} />;

  return (
    <div className="space-y-4 p-3 sm:p-5 lg:p-6 bg-gradient-to-br from-slate-50 to-indigo-50/30 dark:from-slate-950 dark:to-slate-900 min-h-screen" data-testid="reports-page">
      {/* Header */}
      <Card className="p-4 bg-white dark:bg-slate-900 border-0 shadow-sm dark:ring-1 dark:ring-slate-700">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
              <FileBarChart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Laporan Bulanan</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Periode: {periodLabel}{selectedBranch && selectedBranch !== 'all' ? ' · Cabang dipilih' : ' · Semua Cabang'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PeriodPicker year={year} month={month} onChange={onPeriodChange} loading={loading} />
            <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="refresh-reports">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-3xl mb-3">
          <TabsTrigger value="registrations" data-testid="tab-registrations">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Registrasi</span>
            <span className="sm:hidden">Reg</span>
            {regData?.summary?.total > 0 && <Badge className="ml-1.5 bg-green-500 text-white text-[9px] px-1.5">{regData.summary.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="late" data-testid="tab-late">
            <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Telat Bayar</span>
            <span className="sm:hidden">Telat</span>
            {lateData?.summary?.total > 0 && <Badge className="ml-1.5 bg-red-500 text-white text-[9px] px-1.5">{lateData.summary.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="churn" data-testid="tab-churn">
            <UserMinus className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Berhenti</span>
            <span className="sm:hidden">Stop</span>
            {churnData?.summary?.total > 0 && <Badge className="ml-1.5 bg-orange-500 text-white text-[9px] px-1.5">{churnData.summary.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            <Ticket className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Problem Tiket</span>
            <span className="sm:hidden">Tiket</span>
            {ticketData?.summary?.total > 0 && <Badge className="ml-1.5 bg-purple-500 text-white text-[9px] px-1.5">{ticketData.summary.total}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* === TAB 1: REGISTRATIONS === */}
        <TabsContent value="registrations" className="space-y-3">
          <TabHeader title="Registrasi Pelanggan" icon={Users}
            subtitle={`Pelanggan baru yang mendaftar di ${periodLabel}`}
            rows={regData?.rows || []}
            onExport={() => exportToExcel(`registrasi-${year}-${String(month).padStart(2,'0')}`, regData?.rows || [], regCols)}
            onPrint={() => printReport(`Laporan Registrasi Pelanggan — ${periodLabel}`, periodLabel, regData?.rows || [], regCols,
              `<b>Total pendaftar: ${regData?.summary?.total || 0}</b>`)} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Users} label="Total Pendaftar" value={regData?.summary?.total || 0} color="emerald" sub={`Bulan ${periodLabel}`} />
            <StatCard icon={Calendar} label="Rata-rata/Hari" value={regData?.daily_chart?.length ? Math.round((regData.summary.total || 0) / new Date(year, month, 0).getDate()) : 0} color="blue" />
            <StatCard icon={ArrowUpRight} label="Cabang Aktif" value={regData?.breakdown_by_branch?.length || 0} color="violet" />
            <StatCard icon={TrendingUp} label="Paket Populer" value={regData?.breakdown_by_plan?.[0]?.plan || '-'} color="amber" sub={regData?.breakdown_by_plan?.[0] ? `${regData.breakdown_by_plan[0].count} pelanggan` : ''} />
          </div>

          {regData?.breakdown_by_branch?.length > 0 && (
            <div className="grid md:grid-cols-3 gap-3">
              {[['Per Cabang', regData.breakdown_by_branch, 'branch'], ['Per Pemilik', regData.breakdown_by_owner, 'owner'], ['Per Paket', regData.breakdown_by_plan, 'plan']].map(([title, data, key]) => (
                <Card key={title} className="p-4 bg-white dark:bg-slate-900 dark:ring-1 dark:ring-slate-700 border-0 shadow-sm">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">{title}</h3>
                  <div className="space-y-1.5">
                    {(data || []).slice(0, 5).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-slate-700 dark:text-slate-300 truncate">{row[key] || '-'}</span>
                        <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 text-[10px]">{row.count}</Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {renderTable(regData?.rows || [], regCols, 'table-registrations')}
        </TabsContent>

        {/* === TAB 2: LATE PAYMENTS === */}
        <TabsContent value="late" className="space-y-3">
          <TabHeader title="Pelanggan Telat Bayar" icon={AlertCircle}
            subtitle={`Tagihan bermasalah dengan jatuh tempo di ${periodLabel}`}
            rows={lateData?.rows || []}
            onExport={() => exportToExcel(`telat-bayar-${year}-${String(month).padStart(2,'0')}`, lateData?.rows || [], lateCols)}
            onPrint={() => printReport(`Laporan Telat Bayar — ${periodLabel}`, periodLabel, lateData?.rows || [], lateCols,
              `<b>Total: ${lateData?.summary?.total || 0} invoice | Total jumlah: ${fmtIDR(lateData?.summary?.total_amount)}</b>`)} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={AlertCircle} label="Total Invoice" value={lateData?.summary?.total || 0} color="red" />
            <StatCard icon={AlertCircle} label="Belum Lunas" value={lateData?.summary?.unpaid_overdue || 0} color="orange" sub="Lewat jatuh tempo" />
            <StatCard icon={Calendar} label="Telat Bayar" value={lateData?.summary?.paid_late || 0} color="amber" sub="Lunas tapi terlambat" />
            <StatCard icon={TrendingUp} label="Total Nilai" value={fmtIDR(lateData?.summary?.total_amount)} color="red" />
          </div>

          {renderTable(lateData?.rows || [], lateCols, 'table-late')}
        </TabsContent>

        {/* === TAB 3: CHURN === */}
        <TabsContent value="churn" className="space-y-3">
          <TabHeader title="Pelanggan Berhenti Berlangganan" icon={UserMinus}
            subtitle={`Pelanggan yang dinonaktifkan di ${periodLabel}`}
            rows={churnData?.rows || []}
            onExport={() => exportToExcel(`berhenti-${year}-${String(month).padStart(2,'0')}`, churnData?.rows || [], churnCols)}
            onPrint={() => printReport(`Laporan Pelanggan Berhenti — ${periodLabel}`, periodLabel, churnData?.rows || [], churnCols,
              `<b>Total berhenti: ${churnData?.summary?.total || 0}</b>`)} />

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard icon={UserMinus} label="Total Berhenti" value={churnData?.summary?.total || 0} color="red" sub={`Bulan ${periodLabel}`} />
            <StatCard icon={ArrowUpRight} label="Cabang Terdampak" value={churnData?.breakdown_by_branch?.length || 0} color="orange" />
            <StatCard icon={TrendingUp} label="Paket Paling Ditinggal" value={churnData?.breakdown_by_plan?.[0]?.plan || '-'} color="amber" sub={churnData?.breakdown_by_plan?.[0] ? `${churnData.breakdown_by_plan[0].count} pelanggan` : ''} />
          </div>

          {renderTable(churnData?.rows || [], churnCols, 'table-churn')}
        </TabsContent>

        {/* === TAB 4: TICKET PROBLEMS === */}
        <TabsContent value="tickets" className="space-y-3">
          <TabHeader title="Laporan Problem Tiket" icon={Ticket}
            subtitle={`Tiket gangguan/problem yang dibuka di ${periodLabel}`}
            rows={ticketData?.rows || []}
            onExport={() => exportToExcel(`tiket-${year}-${String(month).padStart(2,'0')}`, ticketData?.rows || [], ticketCols)}
            onPrint={() => printReport(`Laporan Problem Tiket — ${periodLabel}`, periodLabel, ticketData?.rows || [], ticketCols,
              `<b>Total: ${ticketData?.summary?.total || 0} tiket | Selesai: ${ticketData?.summary?.resolved || 0} | Pending: ${ticketData?.summary?.pending || 0} | Avg waktu selesai: ${ticketData?.summary?.avg_resolution_hours || 0} jam</b>`)} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Ticket} label="Total Tiket" value={ticketData?.summary?.total || 0} color="indigo" sub={`Bulan ${periodLabel}`} />
            <StatCard icon={CheckCircle2} label="Selesai" value={ticketData?.summary?.resolved || 0} color="emerald" />
            <StatCard icon={Clock} label="Pending" value={ticketData?.summary?.pending || 0} color="amber" />
            <StatCard icon={TrendingUp} label="Avg Waktu (jam)" value={ticketData?.summary?.avg_resolution_hours || 0} color="blue" sub="Pembuatan → Selesai" />
          </div>

          {ticketData?.summary?.total > 0 && (
            <div className="grid md:grid-cols-3 gap-3">
              {[
                ['Per Cabang', ticketData.breakdown_by_branch || [], 'branch'],
                ['Per Kategori', ticketData.breakdown_by_category || [], 'category'],
                ['Per Teknisi', ticketData.breakdown_by_technician || [], 'technician'],
              ].map(([title, data, key]) => (
                <Card key={title} className="p-4 bg-white dark:bg-slate-900 dark:ring-1 dark:ring-slate-700 border-0 shadow-sm">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                    {title === 'Per Cabang' && <ArrowUpRight className="w-3 h-3" />}
                    {title === 'Per Kategori' && <Wrench className="w-3 h-3" />}
                    {title === 'Per Teknisi' && <Users className="w-3 h-3" />}
                    {title}
                  </h3>
                  <div className="space-y-1.5">
                    {(data || []).slice(0, 6).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-slate-700 dark:text-slate-300 truncate">{row[key] || '-'}</span>
                        <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-[10px]">{row.count}</Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {ticketData?.breakdown_by_status?.length > 0 && (
            <Card className="p-4 bg-white dark:bg-slate-900 dark:ring-1 dark:ring-slate-700 border-0 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Status Distribusi</h3>
              <div className="flex flex-wrap gap-2">
                {ticketData.breakdown_by_status.map((s, i) => {
                  const sl = (s.status || '').toLowerCase();
                  const color = sl.includes('resolv') || sl.includes('clos') || sl.includes('done') || sl.includes('selesai')
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : sl.includes('pend') || sl.includes('progress')
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
                  return (
                    <Badge key={i} className={`${color} text-[11px] px-2 py-1`}>{s.status}: {s.count}</Badge>
                  );
                })}
              </div>
            </Card>
          )}

          {renderTable(ticketData?.rows || [], ticketCols, 'table-tickets')}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
